/**
 * InquiryForm Component
 * Contact vendor form
 * BP-08-04: Marketplace Frontend (Family)
 */

import React, { useState } from 'react';
import { Loader2, Send, Mail, Phone } from 'lucide-react';
import { useSubmitInquiry } from '@/features/marketplace/hooks/useMarketplace';
import type { InquiryRequest } from '@/features/marketplace/types/marketplaceTypes';

interface InquiryFormProps {
  vendorId: string;
  vendorName: string;
  productId?: string;
  productName?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const InquiryForm: React.FC<InquiryFormProps> = ({
  vendorId,
  vendorName,
  productId,
  productName,
  onSuccess,
  onCancel,
}) => {
  const [formData, setFormData] = useState({
    subject: productName ? `Inquiry about ${productName}` : '',
    message: '',
    name: '',
    email: '',
    phone: '',
    preferredContact: 'email' as 'email' | 'phone',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const { mutate: submitInquiry, isPending, isSuccess } = useSubmitInquiry({
    onSuccess: () => {
      onSuccess?.();
    },
  });
  
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };
  
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.length < 20) {
      newErrors.message = 'Message must be at least 20 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    const data: InquiryRequest = {
      vendorId,
      productId,
      subject: formData.subject.trim(),
      message: formData.message.trim(),
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim() || undefined,
      preferredContact: formData.preferredContact,
    };
    
    submitInquiry(data);
  };
  
  if (isSuccess) {
    return (
      <div className="text-center py-8">
        <div className="
          w-16 h-16 rounded-full
          bg-sage/20 text-sage
          flex items-center justify-center
          mx-auto mb-4
        ">
          <Send className="w-8 h-8" />
        </div>
        <h3 className="font-display font-semibold text-xl text-navy mb-2">
          Message Sent!
        </h3>
        <p className="text-warm-gray mb-6">
          {vendorName} will receive your inquiry and respond to you soon.
        </p>
        <button
          onClick={onCancel}
          className="
            px-6 py-2.5 rounded-lg
            bg-sage text-white font-semibold
            hover:bg-sage-dark
            transition-colors duration-200
          "
        >
          Close
        </button>
      </div>
    );
  }
  
  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="text-center mb-6">
        <h3 className="font-display font-semibold text-xl text-navy">
          Contact {vendorName}
        </h3>
        {productName && (
          <p className="text-warm-gray text-sm mt-1">
            Regarding: {productName}
          </p>
        )}
      </div>
      
      {/* Name */}
      <div>
        <label htmlFor="inquiry-name" className="block text-sm font-medium text-navy mb-1.5">
          Your Name <span className="text-terracotta">*</span>
        </label>
        <input
          id="inquiry-name"
          name="name"
          type="text"
          value={formData.name}
          onChange={handleChange}
          placeholder="Enter your name"
          className={`
            w-full px-4 py-3 rounded-lg
            bg-cream border-2 border-transparent
            text-navy placeholder-warm-gray
            focus:border-sage focus:outline-none
            transition-colors duration-200
            ${errors.name ? 'border-terracotta' : ''}
          `}
        />
        {errors.name && (
          <p className="mt-1 text-sm text-terracotta">{errors.name}</p>
        )}
      </div>
      
      {/* Email */}
      <div>
        <label htmlFor="inquiry-email" className="block text-sm font-medium text-navy mb-1.5">
          Email Address <span className="text-terracotta">*</span>
        </label>
        <input
          id="inquiry-email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="your@email.com"
          className={`
            w-full px-4 py-3 rounded-lg
            bg-cream border-2 border-transparent
            text-navy placeholder-warm-gray
            focus:border-sage focus:outline-none
            transition-colors duration-200
            ${errors.email ? 'border-terracotta' : ''}
          `}
        />
        {errors.email && (
          <p className="mt-1 text-sm text-terracotta">{errors.email}</p>
        )}
      </div>
      
      {/* Phone */}
      <div>
        <label htmlFor="inquiry-phone" className="block text-sm font-medium text-navy mb-1.5">
          Phone Number <span className="text-warm-gray font-normal">(optional)</span>
        </label>
        <input
          id="inquiry-phone"
          name="phone"
          type="tel"
          value={formData.phone}
          onChange={handleChange}
          placeholder="(555) 555-5555"
          className="
            w-full px-4 py-3 rounded-lg
            bg-cream border-2 border-transparent
            text-navy placeholder-warm-gray
            focus:border-sage focus:outline-none
            transition-colors duration-200
          "
        />
      </div>
      
      {/* Preferred Contact */}
      <div>
        <label className="block text-sm font-medium text-navy mb-2">
          Preferred Contact Method
        </label>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setFormData((prev) => ({ ...prev, preferredContact: 'email' }))}
            className={`
              flex-1 px-4 py-2.5 rounded-lg
              flex items-center justify-center gap-2
              font-medium text-sm
              transition-colors duration-200
              ${formData.preferredContact === 'email'
                ? 'bg-sage text-white'
                : 'bg-cream text-navy-light hover:bg-cream-dark'
              }
            `}
          >
            <Mail className="w-4 h-4" />
            Email
          </button>
          <button
            type="button"
            onClick={() => setFormData((prev) => ({ ...prev, preferredContact: 'phone' }))}
            className={`
              flex-1 px-4 py-2.5 rounded-lg
              flex items-center justify-center gap-2
              font-medium text-sm
              transition-colors duration-200
              ${formData.preferredContact === 'phone'
                ? 'bg-sage text-white'
                : 'bg-cream text-navy-light hover:bg-cream-dark'
              }
            `}
          >
            <Phone className="w-4 h-4" />
            Phone
          </button>
        </div>
      </div>
      
      {/* Subject */}
      <div>
        <label htmlFor="inquiry-subject" className="block text-sm font-medium text-navy mb-1.5">
          Subject <span className="text-terracotta">*</span>
        </label>
        <input
          id="inquiry-subject"
          name="subject"
          type="text"
          value={formData.subject}
          onChange={handleChange}
          placeholder="What is your inquiry about?"
          className={`
            w-full px-4 py-3 rounded-lg
            bg-cream border-2 border-transparent
            text-navy placeholder-warm-gray
            focus:border-sage focus:outline-none
            transition-colors duration-200
            ${errors.subject ? 'border-terracotta' : ''}
          `}
        />
        {errors.subject && (
          <p className="mt-1 text-sm text-terracotta">{errors.subject}</p>
        )}
      </div>
      
      {/* Message */}
      <div>
        <label htmlFor="inquiry-message" className="block text-sm font-medium text-navy mb-1.5">
          Message <span className="text-terracotta">*</span>
        </label>
        <textarea
          id="inquiry-message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Describe what you're looking for, any specific requirements, or questions you have..."
          rows={5}
          className={`
            w-full px-4 py-3 rounded-lg
            bg-cream border-2 border-transparent
            text-navy placeholder-warm-gray
            focus:border-sage focus:outline-none
            transition-colors duration-200
            resize-none
            ${errors.message ? 'border-terracotta' : ''}
          `}
        />
        {errors.message && (
          <p className="mt-1 text-sm text-terracotta">{errors.message}</p>
        )}
      </div>
      
      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="
              px-6 py-2.5 rounded-lg
              text-navy font-medium
              hover:bg-cream
              transition-colors duration-200
            "
          >
            Cancel
          </button>
        )}
        
        <button
          type="submit"
          disabled={isPending}
          className="
            px-6 py-2.5 rounded-lg
            bg-sage text-white font-semibold
            hover:bg-sage-dark
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-colors duration-200
            flex items-center gap-2
          "
        >
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Send Message
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default InquiryForm;

